import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { userApi } from '../../api/user.api';
import { authApi } from '../../api/auth.api';
import { useAuth } from '../../store/AuthContext';
import { Camera, LockSimple, CheckCircle } from '@phosphor-icons/react';
import { useUI } from '../../store/UIContext';
import { ROLE_LABELS, getDashboardRoute } from '../../utils/roleHelpers';
import { toPersian } from '../../utils/persianNumber';
import { Skeleton, Btn, Badge, PageHeader, Spinner, PasswordStrengthMeter } from '../../components/ui/index';
import { BASE_MEDIA_URL } from '../../api/client';
import { phoneError, emailError, phoneInputChange } from '../../utils/validators';
import './EditProfilePage.css';

// ─── Canvas-based avatar cropper (circular) ───────────────────────────
function AvatarCropper({ src, onCancel, onConfirm }) {
  const SIZE = 220;
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });

  const baseScale = nat.w && nat.h ? Math.max(SIZE / nat.w, SIZE / nat.h) : 1;
  const dw = nat.w * baseScale * zoom;
  const dh = nat.h * baseScale * zoom;

  const clamp = (x, y) => ({
    x: Math.min(0, Math.max(SIZE - dw, x)),
    y: Math.min(0, Math.max(SIZE - dh, y)),
  });

  const onLoad = (e) => {
    setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    setOff({ x: 0, y: 0 });
  };

  const onMouseDown = (e) => {
    drag.current = { sx: e.clientX - off.x, sy: e.clientY - off.y };
  };
  const onMouseMove = (e) => {
    if (!drag.current) return;
    setOff(clamp(e.clientX - drag.current.sx, e.clientY - drag.current.sy));
  };
  const onMouseUp = () => { drag.current = null; };

  // Touch support
  const onTouchStart = (e) => {
    const t = e.touches[0];
    drag.current = { sx: t.clientX - off.x, sy: t.clientY - off.y };
  };
  const onTouchMove = (e) => {
    if (!drag.current) return;
    const t = e.touches[0];
    setOff(clamp(t.clientX - drag.current.sx, t.clientY - drag.current.sy));
  };

  const handleZoom = (e) => {
    const newZoom = Number(e.target.value);
    setZoom(newZoom);
    setOff((prev) => clamp(prev.x, prev.y));
  };

  const confirm = () => {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    const img = imgRef.current;
    const sc = baseScale * zoom;
    ctx.drawImage(img, -off.x / sc, -off.y / sc, SIZE / sc, SIZE / sc, 0, 0, SIZE, SIZE);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    onConfirm(dataUrl);
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        تصویر را بکشید و با اسلایدر زوم کنید
      </p>
      {/* Circular crop viewport */}
      <div
        style={{
          width: SIZE, height: SIZE, borderRadius: '50%',
          overflow: 'hidden', cursor: 'grab', userSelect: 'none',
          margin: '0 auto 16px', position: 'relative',
          background: '#0f172a',
          border: '3px solid #29AAE1',
          boxShadow: '0 0 0 4px rgba(35,58,122,0.2)',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onMouseUp}
      >
        <img
          ref={imgRef}
          src={src}
          onLoad={onLoad}
          alt="crop"
          draggable={false}
          style={{
            position: 'absolute',
            width: dw, height: dh,
            left: off.x, top: off.y,
            objectFit: 'cover',
          }}
        />
      </div>
      {/* Zoom slider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20, width: SIZE, margin: '0 auto 20px' }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>کوچک</span>
        <input
          type="range" min={1} max={3} step={0.01}
          value={zoom}
          onChange={handleZoom}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>بزرگ</span>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          onClick={onCancel}
          style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8faff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}
        >
          انصراف
        </button>
        <button
          onClick={confirm}
          style={{ padding: '9px 24px', borderRadius: 8, background: '#233A7A', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}
        >
          تأیید برش
        </button>
      </div>
    </div>
  );
}

// ─── Contact verification card (email or phone) ───────────────────────
function ContactVerificationCard({ type, label, currentValue, isRegistered, username, addToast, onVerified, startOpen }) {
  const [editMode, setEditMode] = useState(!!startOpen && !isRegistered);
  const [newValue, setNewValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [registered, setRegistered] = useState(isRegistered);

  // Sync from parent when props update
  useEffect(() => { setRegistered(isRegistered); }, [isRegistered]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const validationErr = newValue.trim() ? (type === 'email' ? emailError(newValue) : phoneError(newValue)) : '';

  const sendCode = async () => {
    if (sending || countdown > 0) return;
    if (validationErr) return;
    setSending(true);
    try {
      // If user entered a new value, save it to backend first
      if (newValue.trim()) {
        try {
          if (type === 'email') await userApi.editEmail(newValue.trim());
          else await userApi.editPhoneNumber(newValue.trim());
        } catch {
          // Non-fatal: backend may not have these endpoints; continue to send code
        }
      }
      await authApi.generateAuthCode(username, type);
      setCountdown(90);
      setOtpSent(true);
      addToast('کد تایید ارسال شد', 'success');
    } catch {
      addToast('خطا در ارسال کد تایید', 'error');
    }
    setSending(false);
  };

  const verify = async () => {
    if (otp.length < 7) return;
    setVerifying(true);
    try {
      await authApi.registerInfo({ auth_code: otp, type, self_registration: 1, username });
      setRegistered(true);
      setOtpSent(false);
      setEditMode(false);
      setOtp('');
      setNewValue('');
      onVerified();
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) addToast('کد وارد شده اشتباه است', 'error');
      else if (status === 501) addToast('کد منقضی شده — مجدداً ارسال کنید', 'error');
      else addToast('خطا در تایید کد', 'error');
    }
    setVerifying(false);
  };

  const cancel = () => {
    setEditMode(false);
    setOtpSent(false);
    setOtp('');
    setNewValue('');
  };

  return (
    <div className="pep-card" style={startOpen && !registered ? { boxShadow: '0 0 0 2px #29AAE1' } : undefined}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#334155' }}>{label}</h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          background: registered ? '#f0fdf4' : '#fef2f2',
          color: registered ? '#16a34a' : '#dc2626',
          border: `1px solid ${registered ? '#bbf7d0' : '#fecaca'}`,
        }}>
          {registered ? '✓ تایید شده' : '✗ تایید نشده'}
        </span>
      </div>

      {/* Current value */}
      <div style={{
        padding: '9px 14px', borderRadius: 8, background: '#f8fafc',
        fontSize: 13, color: currentValue ? '#334155' : '#94a3b8',
        marginBottom: 14, direction: 'ltr', textAlign: 'left',
        fontFamily: 'monospace', border: '1px solid #e2e8f0',
      }}>
        {currentValue || `هنوز ${label} ثبت نشده`}
      </div>

      {!editMode ? (
        <Btn size="sm" variant="secondary" onClick={() => setEditMode(true)}>
          {registered ? `تغییر ${label}` : `ثبت و تایید ${label}`}
        </Btn>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* New value input + Send code */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type={type === 'email' ? 'email' : 'tel'}
              value={newValue}
              onChange={type === 'phone' ? phoneInputChange((e) => setNewValue(e.target.value)) : (e) => setNewValue(e.target.value)}
              inputMode={type === 'phone' ? 'numeric' : undefined}
              maxLength={type === 'phone' ? 11 : undefined}
              placeholder={currentValue ? `${label} جدید (اختیاری)` : `${label} را وارد کنید`}
              dir="ltr"
              style={{
                flex: 1, padding: '9px 12px', border: `1.5px solid ${validationErr ? '#fca5a5' : '#e2e8f0'}`,
                borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none',
              }}
            />
            <Btn
              size="sm"
              onClick={sendCode}
              loading={sending}
              disabled={countdown > 0 || !!validationErr}
            >
              {countdown > 0 ? `${countdown}ث` : 'ارسال کد'}
            </Btn>
          </div>
          {validationErr && <p style={{ fontSize: 11, color: '#dc2626', margin: 0 }}>{validationErr}</p>}

          {/* OTP input + Verify */}
          {otpSent && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 7))}
                placeholder="کد ۷ رقمی را وارد کنید"
                dir="ltr"
                maxLength={7}
                style={{
                  flex: 1, padding: '9px 12px', border: '1.5px solid #B7C9EC',
                  borderRadius: 8, fontSize: 15, fontFamily: 'monospace',
                  letterSpacing: 6, textAlign: 'center', outline: 'none',
                }}
              />
              <Btn
                size="sm"
                onClick={verify}
                loading={verifying}
                disabled={otp.length < 7}
              >
                تایید
              </Btn>
            </div>
          )}

          {/* Resend hint */}
          {countdown > 0 && (
            <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>
              ارسال مجدد تا {countdown} ثانیه دیگر
            </p>
          )}

          <button
            onClick={cancel}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 12, cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit', padding: 0 }}
          >
            انصراف
          </button>
        </div>
      )}
    </div>
  );
}

// ─── ChangePasswordCard ────────────────────────────────────────────────
// Self-service password change, available to every role via change_my_
// password (unlike change_user_password/change_employee_password, which
// are manager_employee/dispatcher acting on someone ELSE's account).
function ChangePasswordCard({ addToast }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSave = currentPassword && newPassword && confirmPassword && !mismatch;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await userApi.changeMyPassword({ current_password: currentPassword, new_password: newPassword });
      addToast('رمز عبور با موفقیت تغییر کرد', 'success');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) addToast('رمز عبور فعلی اشتباه است', 'error');
      else addToast('خطا در تغییر رمز عبور', 'error');
    }
    setSaving(false);
  };

  const inputStyleLocal = { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  return (
    <div className="pep-card">
      <h3 className="pep-card-title">تغییر رمز عبور</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>رمز عبور فعلی</label>
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} style={inputStyleLocal} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>رمز عبور جدید</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyleLocal} />
          <PasswordStrengthMeter password={newPassword} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>تکرار رمز عبور جدید</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ ...inputStyleLocal, border: `1.5px solid ${mismatch ? '#fca5a5' : '#e2e8f0'}` }} />
          {mismatch && <p style={{ color: '#dc2626', fontSize: 12, margin: '6px 0 0' }}>رمز عبور و تکرار آن یکسان نیستند.</p>}
        </div>
        <Btn onClick={save} loading={saving} disabled={!canSave} style={{ alignSelf: 'flex-start' }}>ذخیره رمز عبور جدید</Btn>
      </div>
    </div>
  );
}

// ─── ProfilePage ─────────────────────────────────────────────────────
export function ProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (!user) return;
    const apis = {
      simple_user: userApi.getUserTicketCount,
      manager_user: userApi.getManagerTicketCount,
      dispatcher: userApi.getDispatcherTicketCount,
      manager_employee: userApi.getManagerEmployeeTicketCount,
      simple_employee: userApi.getSimpleEmployeeTicketCount,
    };
    const fn = apis[user.type];
    if (fn) fn().then((res) => setCounts(res.data)).catch(() => {}).finally(() => setLoading(false));
    else setLoading(false);
  }, [user]);

  if (!user) return null;
  const imgSrc = !avatarError && user.image_name ? BASE_MEDIA_URL + user.image_name : null;

  return (
    <div className="fade-in">
      <PageHeader title="پروفایل کاربری" />
      <div style={{ maxWidth: 680, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Header card */}
        <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(35,58,122,0.1)', overflow: 'hidden', boxShadow: '0 4px 16px rgba(35,58,122,0.08)' }}>
          <div style={{ height: 100, background: 'linear-gradient(135deg,#1e40af,#29AAE1,#5C7DBE)' }} />
          <div style={{ padding: '0 28px 28px', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div style={{ marginTop: -50, position: 'relative' }}>
                <div style={{ width: 90, height: 90, borderRadius: '50%', border: '4px solid #fff', background: imgSrc ? 'none' : '#29AAE1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                  {imgSrc
                    ? <img src={imgSrc} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setAvatarError(true)} />
                    : <span style={{ color: '#fff', fontSize: 32, fontWeight: 700 }}>{user.username?.slice(0, 2)}</span>}
                </div>
              </div>
              <Btn size="sm" variant="secondary" onClick={() => navigate('/profile/edit')}>ویرایش پروفایل</Btn>
            </div>
            <div style={{ marginTop: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>
                {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
              </h2>
              <Badge variant="blue">{ROLE_LABELS[user.type] || user.type}</Badge>
            </div>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[1, 2, 3].map((k) => (
              <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
                <Skeleton h={32} style={{ marginBottom: 8 }} />
                <Skeleton h={14} w="60%" />
              </div>
            ))}
          </div>
        ) : counts && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
            {Object.entries(counts).map(([key, val]) => (
              <div key={key} style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(35,58,122,0.1)', padding: '20px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#233A7A' }}>{toPersian(val)}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{key.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EditMyInfoPage ───────────────────────────────────────────────────
export function EditMyInfoPage() {
  const { user, loadProfile, setUser } = useAuth();
  const { addToast } = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  const requirePhone = !!location.state?.requirePhone;
  const fileRef = useRef(null);

  // Loading / saving state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailRegistered, setEmailRegistered] = useState(false);
  const [phoneRegistered, setPhoneRegistered] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null); // existing from server
  const [imgLoadError, setImgLoadError] = useState(false);

  // Avatar cropper state
  const [cropSrc, setCropSrc] = useState(null);       // object URL for cropper
  const [croppedPreview, setCroppedPreview] = useState(null); // data URL after crop
  const [croppedBase64, setCroppedBase64] = useState(null);   // sent to editProfileInfo

  // Load profile on mount — profile endpoint returns registration flags only,
  // actual email/phone values come from separate dedicated endpoints.
  useEffect(() => {
    Promise.all([
      userApi.getProfileInfo(),
      userApi.getEmail().catch(() => ({ data: { email: '' } })),
      userApi.getPhoneNumber().catch(() => ({ data: { phone_number: '' } })),
    ])
      .then(([profileRes, emailRes, phoneRes]) => {
        const d = Array.isArray(profileRes.data) ? profileRes.data[0] : profileRes.data;
        if (!d) return;
        setFirstName(d.first_name || '');
        setLastName(d.last_name || '');
        setUsername(d.username || '');
        setEmail(emailRes.data?.email || '');
        setPhone(phoneRes.data?.phone_number || '');
        setEmailRegistered(!!d.email_registred);
        setPhoneRegistered(!!d.phone_registred);
        if (d.image_name) setCurrentImageUrl(BASE_MEDIA_URL + d.image_name);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // File picker → open cropper
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    e.target.value = '';
  };

  // After crop confirmed → get base64
  const handleCropConfirm = useCallback((dataUrl) => {
    setCroppedPreview(dataUrl);
    setCroppedBase64(dataUrl);
    setCropSrc(null);
    // Release the object URL
    URL.revokeObjectURL(cropSrc);
  }, [cropSrc]);

  const handleCropCancel = () => {
    setCropSrc(null);
    if (cropSrc) URL.revokeObjectURL(cropSrc);
  };

  // Save name + avatar
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        first_name: firstName,
        last_name: lastName,
        image_data: croppedBase64 || null,
      };
      await userApi.editProfileInfo(payload);
      const profile = await loadProfile();
      if (profile) setUser(profile);
      addToast('اطلاعات با موفقیت ذخیره شد', 'success');
      navigate('/profile');
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) addToast('خطای احراز هویت — لطفاً دوباره وارد شوید', 'error');
      else addToast('خطا در ذخیره اطلاعات', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <Spinner size={32} />
      </div>
    );
  }

  const avatarSrc = croppedPreview || (!imgLoadError && currentImageUrl) || null;
  const displayName = firstName && lastName ? `${firstName} ${lastName}` : username;
  const roleLabel = user ? (ROLE_LABELS[user.type] || user.type) : null;
  const dashboardRoute = user ? getDashboardRoute(user.type) : '/home';

  return (
    <div className="fade-in pep-page">
      <div className="pep-container">
        <nav className="pep-breadcrumb" aria-label="مسیر صفحه">
          <Link to={dashboardRoute}>خانه</Link>
          <span>/</span>
          <Link to="/profile">حساب کاربری</Link>
          <span>/</span>
          <span className="current">ویرایش اطلاعات</span>
        </nav>

        <PageHeader
          title="ویرایش اطلاعات"
          subtitle="اطلاعات شخصی و تنظیمات حساب کاربری خود را مدیریت کنید."
        />

        {/* ── Cropper overlay ── */}
        {cropSrc && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(15,23,42,0.88)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}>
            <div style={{
              background: '#fff', borderRadius: 20, padding: 32,
              maxWidth: 380, width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            }}>
              <h3 style={{ margin: '0 0 20px', textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                برش تصویر پروفایل
              </h3>
              <AvatarCropper
                src={cropSrc}
                onCancel={handleCropCancel}
                onConfirm={handleCropConfirm}
              />
            </div>
          </div>
        )}

        <div className="pep-grid">
          {/* ── Left column: identity + account cards ── */}
          <div className="pep-aside">
            <div className="pep-card pep-profile-card">
              <div className="profile-avatar-wrapper" onClick={() => fileRef.current?.click()}>
                <div className="profile-avatar-ring" />
                <div className="profile-avatar">
                  {avatarSrc
                    ? <img src={avatarSrc} alt={username} onError={() => setImgLoadError(true)} />
                    : <Camera size={34} weight="regular" />}
                </div>
                <div className="profile-avatar-overlay">
                  <Camera size={22} weight="regular" />
                </div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />

              <p className="pep-profile-name">{displayName || 'کاربر'}</p>
              {roleLabel && <p className="pep-profile-role">{roleLabel}</p>}

              <button className="pep-change-photo-btn" onClick={() => fileRef.current?.click()}>
                {avatarSrc ? 'تغییر تصویر' : 'انتخاب تصویر'}
              </button>
              {croppedPreview && (
                <p className="pep-photo-ready"><CheckCircle size={12} weight="regular" /> تصویر جدید آماده ذخیره است</p>
              )}
            </div>

            {/* ── Email card ── */}
            <ContactVerificationCard
              type="email"
              label="ایمیل"
              currentValue={email}
              isRegistered={emailRegistered}
              username={username}
              addToast={addToast}
              onVerified={() => {
                setEmailRegistered(true);
                addToast('ایمیل با موفقیت تایید شد', 'success');
              }}
            />

            {/* ── Phone card ── */}
            <ContactVerificationCard
              type="phone"
              label="شماره موبایل"
              currentValue={phone}
              isRegistered={phoneRegistered}
              username={username}
              addToast={addToast}
              startOpen={requirePhone && !phoneRegistered}
              onVerified={async () => {
                setPhoneRegistered(true);
                addToast('شماره موبایل با موفقیت تایید شد', 'success');
                const profile = await loadProfile();
                if (profile) setUser(profile);
              }}
            />

            {/* ── Change password card ── */}
            <ChangePasswordCard addToast={addToast} />
          </div>

          {/* ── Right column: the dominant personal-info form ── */}
          <div className="pep-card">
            <h3 className="pep-card-title">اطلاعات شخصی</h3>
            <div className="profile-form-grid">
              <div>
                <label className="pep-label">نام</label>
                <input
                  type="text"
                  className="form-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="pep-label">نام خانوادگی</label>
                <input
                  type="text"
                  className="form-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="pep-field-full">
                <label className="pep-label">نام کاربری</label>
                <div className="pep-username-wrap">
                  <input type="text" className="form-input" value={username} readOnly />
                  <span className="pep-username-lock"><LockSimple size={15} weight="regular" /></span>
                </div>
                <p className="pep-helper-text">نام کاربری قابل تغییر نمی‌باشد.</p>
              </div>
            </div>
            <div className="pep-actions">
              <button className="pep-btn pep-btn-secondary" onClick={() => navigate('/profile')}>انصراف</button>
              <button className="pep-btn pep-btn-primary" onClick={save} disabled={saving}>
                {saving ? <Spinner size={14} color="#fff" /> : null}
                ذخیره تغییرات
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
